import { c as _c } from "react/compiler-runtime";
import { GetProjectConfigurationResp, GroupByNegativeUserIntentV2Kind, ProjectConfiguration } from '@/api/generated/model';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEY_CONFIGURATION } from '@/constants/query-keys';
import { DEFAULT_IGNORED_GROUPBY_ITEMS } from '@/domains/home/hooks/useGroupByItems';
import { useCallback, useMemo } from 'react';
import { isPlayground as checkIsPlayground } from '@/utils/check-is-playground';
import { useGlobalFiltersContext } from '@/contexts/filters/filter-context';
import { cloneDeep, isEqual } from 'lodash';
import { getConfiguration as getProjectConfiguration, useUpdateConfiguration as useUpdateProjectConfiguration } from '@/api/generated/projects/projects';
export const useConfig = (projectId, onUpdate, t0, t1) => {
  const $ = _c(31);
  const optimisticUpdate = t0 === undefined ? false : t0;
  const enabled = t1 === undefined ? true : t1;
  const {
    aggregateBy
  } = useGlobalFiltersContext();
  let t2;
  if ($[0] !== aggregateBy || $[1] !== projectId) {
    t2 = [QUERY_KEY_CONFIGURATION, projectId, aggregateBy];
    $[0] = aggregateBy;
    $[1] = projectId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const queryKeyProjectConfig = t2;
  const client = useQueryClient();
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = checkIsPlayground();
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const isPlaygroundMode = t3;
  let t4;
  if ($[4] !== projectId) {
    t4 = t5 => {
      const {
        signal
      } = t5;
      return getProjectConfiguration({
        project_id: projectId
      }, {
        signal
      });
    };
    $[4] = projectId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = !!projectId && enabled;
  let t6;
  if ($[6] !== queryKeyProjectConfig || $[7] !== t4 || $[8] !== t5) {
    t6 = {
      queryKey: queryKeyProjectConfig,
      queryFn: t4,
      enabled: t5,
      staleTime: isPlaygroundMode ? Infinity : 0,
      cacheTime: isPlaygroundMode ? Infinity : 0,
      select: _temp4
    };
    $[6] = queryKeyProjectConfig;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    data: configData,
    isLoading
  } = useQuery(t6);
  let t7;
  if ($[10] !== client || $[11] !== configData?.configuration || $[12] !== onUpdate || $[13] !== optimisticUpdate || $[14] !== queryKeyProjectConfig) {
    let t9;
    if ($[16] !== client || $[17] !== configData?.configuration || $[18] !== onUpdate || $[19] !== queryKeyProjectConfig) {
      t9 = (_, t10) => {
        const {
          data: data_1
        } = t10;
        const newconfig_0 = {
          ...data_1,
          aggregates: configData?.configuration.aggregates
        };
        client.setQueryData(queryKeyProjectConfig, {
          configuration: newconfig_0
        });
        onUpdate?.(newconfig_0);
      };
      $[16] = client;
      $[17] = configData?.configuration;
      $[18] = onUpdate;
      $[19] = queryKeyProjectConfig;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    t7 = {
      mutation: {
        onMutate(t8) {
          const {
            data: data_0
          } = t8;
          const newconfig = {
            ...data_0,
            aggregates: configData?.configuration.aggregates
          };
          if (optimisticUpdate) {
            client.setQueryData(queryKeyProjectConfig, {
              configuration: newconfig
            });
          }
        },
        onSuccess: t9
      }
    };
    $[10] = client;
    $[11] = configData?.configuration;
    $[12] = onUpdate;
    $[13] = optimisticUpdate;
    $[14] = queryKeyProjectConfig;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const {
    mutate: _updateConfig,
    isPending: isUpdating
  } = useUpdateProjectConfiguration(t7);
  let t8;
  if ($[21] !== _updateConfig || $[22] !== client || $[23] !== configData?.configuration || $[24] !== queryKeyProjectConfig) {
    t8 = (...t9) => {
      const params = t9;
      const [first, ...rest] = params;
      if (!first?.data) {
        if (!isPlaygroundMode) {
          _updateConfig(...params);
        }
        return;
      }
      const currentConfig = configData?.configuration ?? {};
      const incomingData = first.data as Record<string, unknown>;
      const patch = {};
      for (const [key, value] of Object.entries(incomingData)) {
        if (key === "aggregates") {
          continue;
        }
        const currentValue = (currentConfig as Record<string, unknown>)[key];
        if (!isEqual(value, currentValue)) {
          patch[key] = value;
        }
      }
      if (Object.keys(patch).length === 0) {
        return;
      }
      if (isPlaygroundMode) {
        client.setQueryData(queryKeyProjectConfig, old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            configuration: {
              ...old.configuration,
              ...patch
            }
          };
        });
        return;
      }
      const clonedFirst = cloneDeep(first);
      clonedFirst.data = patch;
      _updateConfig(clonedFirst, ...rest);
    };
    $[21] = _updateConfig;
    $[22] = client;
    $[23] = configData?.configuration;
    $[24] = queryKeyProjectConfig;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const updateConfig = t8;
  const t9 = configData?.configuration;
  let t10;
  if ($[26] !== isLoading || $[27] !== isUpdating || $[28] !== t9 || $[29] !== updateConfig) {
    t10 = {
      data: t9,
      updateConfig,
      isLoadingData: isLoading,
      isUpdating
    };
    $[26] = isLoading;
    $[27] = isUpdating;
    $[28] = t9;
    $[29] = updateConfig;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
};

/**
 * convert negative_intent and negative_intent_v2 to negative_user_intent_v2
 */
function normalizeItem(item: string) {
  if (item === 'negative_intent' || item === 'negative_intent_v2') {
    return GroupByNegativeUserIntentV2Kind.negative_user_intent_v2;
  }
  return item;
}
function _temp(item) {
  return normalizeItem(item);
}
function _temp2(item_0) {
  return normalizeItem(item_0);
}
function _temp3(item_1) {
  return normalizeItem(item_1);
}
function _temp4(data) {
  const verticalNavbar = data.configuration.user_intelligence?.vertical_navbar_items ?? [];
  const sortedItems = data.configuration.user_intelligence?.group_by_sort ?? [];
  const ignoredItems = data.configuration.user_intelligence?.group_by_ignore ?? [];
  const normalizedVerticals = verticalNavbar.map(_temp);
  const normalizedSortedItems = sortedItems.map(_temp2);
  const normalizedIgnoredItems = ignoredItems.map(_temp3);
  return {
    ...data,
    configuration: {
      ...data.configuration,
      user_intelligence: {
        ...data.configuration.user_intelligence,
        group_by_sort: normalizedSortedItems.length ? normalizedSortedItems : undefined,
        vertical_navbar_items: normalizedVerticals.length ? normalizedVerticals : undefined,
        group_by_ignore: normalizedIgnoredItems.length ? normalizedIgnoredItems : DEFAULT_IGNORED_GROUPBY_ITEMS
      }
    }
  } satisfies GetProjectConfigurationResp;
}